import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppScreen } from '../../components/AppScreen';
import { AttendanceMonthCalendar } from '../../components/AttendanceMonthCalendar';
import { AttendanceStatusBadge } from '../../components/AttendanceStatusBadge';
import { EmptyState } from '../../components/EmptyState';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { SegmentedControl } from '../../components/SegmentedControl';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import type { ColorPalette } from '../../constants/colors';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { useAttendanceHistory } from '../../hooks/useAttendanceHistory';
import type { AttendanceHistoryItem } from '../../types/attendance';
import {
  attendanceToneColor,
  formatAttendanceClock,
  formatAttendanceListDate,
  monthDateRange,
  parseYearMonth,
  resolveAttendanceCalendarTone,
  type AttendanceCalendarTone,
} from '../../utils/attendanceCalendar';
import { holidaysByDate } from '../../utils/attendanceHolidays';
import { createDefaultAttendanceFilters } from '../../utils/attendanceFilters';
import { resolveAttendanceLocationMode } from '../../utils/attendanceLocationMode';
import {
  formatAttendanceDate,
  formatAttendanceTimestamp,
  formatAttendanceWeekday,
  formatDurationMinutes,
} from '../../utils/dateTime';
import {
  resolveCheckedOutWorkHoursCoverage,
  workHoursCoverageLabel,
} from '../../utils/workHoursCoverage';
import {
  checkedOutTotalWorkMinutes,
  formatPolicyHours,
  isAttendanceCheckedOut,
  formatTotalWorkHours,
  resolveTotalHoursCoverage,
} from '../../utils/totalHours';

type Props = NativeStackScreenProps<RootStackParamList, 'AttendanceHistory'>;
type PeriodTab = 'Daily' | 'Weekly' | 'Monthly';
type EmployeeViewTab = 'History' | 'Calendar';

function locationModeLabel(item: AttendanceHistoryItem) {
  return resolveAttendanceLocationMode({ attendanceMode: item.attendanceMode });
}

/** Total Work Hours only once the day is checked out; null (dash) before. */
function historyTotalWorkMinutes(item: AttendanceHistoryItem): number | null {
  return checkedOutTotalWorkMinutes(item);
}

function rowKey(item: AttendanceHistoryItem) {
  return String(item.attendanceId ?? item.attendanceDate);
}

function FilterGlyph({ color }: { color: string }) {
  return (
    <View style={styles.filterGlyph} accessibilityElementsHidden>
      <View style={[styles.filterFunnel, { borderTopColor: color }]} />
      <View style={[styles.filterStem, { backgroundColor: color }]} />
    </View>
  );
}

function DetailRowIcon({
  kind,
  color,
}: {
  kind: 'checkIn' | 'checkOut' | 'status';
  color: string;
}) {
  if (kind === 'status') {
    return (
      <View style={styles.detailIcon} accessibilityElementsHidden>
        <View style={[styles.clipboardBox, { borderColor: color }]}>
          <View style={[styles.clipboardClip, { backgroundColor: color }]} />
          <View style={[styles.clipboardLine, { backgroundColor: color }]} />
          <View style={[styles.clipboardLine, { backgroundColor: color }]} />
        </View>
      </View>
    );
  }
  return (
    <View style={styles.detailIcon} accessibilityElementsHidden>
      <View style={[styles.clockOuter, { borderColor: color }]}>
        <View style={[styles.clockHand, { backgroundColor: color }]} />
        <View style={[styles.clockHandShort, { backgroundColor: color }]} />
      </View>
    </View>
  );
}

function statusPresentation(
  item: AttendanceHistoryItem,
  palette: ColorPalette,
): {
  tone: AttendanceCalendarTone;
  useBadge: boolean;
  color: string;
} {
  const tone = resolveAttendanceCalendarTone(item);
  const useBadge = tone === 'wfh' || tone === 'leave' || tone === 'permission';
  return {
    tone,
    useBadge,
    color: attendanceToneColor(tone, palette),
  };
}

function formatHistoryDuration(value: number | null | undefined): string {
  const formatted = formatDurationMinutes(value ?? null);
  return formatted === 'Not available' ? '--' : formatted;
}

function EmployeeHistoryRow({
  item,
  onPress,
  palette,
}: {
  item: AttendanceHistoryItem;
  onPress: () => void;
  palette: ColorPalette;
}) {
  const presentation = statusPresentation(item, palette);
  const worked = formatHistoryDuration(item.workedMinutes);
  const breakHours = formatHistoryDuration(item.breakMinutes);
  const hoursCoverage = resolveCheckedOutWorkHoursCoverage(
    isAttendanceCheckedOut(item),
    item.workedMinutes,
    item.expectedWorkMinutes,
  );
  const hoursCoverageText = workHoursCoverageLabel(hoursCoverage);
  const hoursColor =
    hoursCoverage === 'covered'
      ? palette.success
      : hoursCoverage === 'not_covered'
      ? palette.error
      : palette.textPrimary;
  const totalHoursCoverage = resolveTotalHoursCoverage(
    historyTotalWorkMinutes(item),
    item.spreadMinutes,
  );
  const totalHoursColor =
    totalHoursCoverage === 'not_covered' ? palette.error : palette.textPrimary;

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`${formatAttendanceListDate(
        item.attendanceDate,
      )}, status ${item.statusLabel}`}
      onPress={onPress}
      style={[
        styles.employeeRow,
        { backgroundColor: palette.surface, borderColor: palette.border },
      ]}
    >
      <View style={styles.employeeRowHeader}>
        <Text style={[styles.employeeDate, { color: palette.textPrimary }]}>
          {formatAttendanceListDate(item.attendanceDate)}
        </Text>
        {presentation.useBadge ? (
          <AttendanceStatusBadge label={item.statusLabel} decorative />
        ) : (
          <Text
            style={[styles.employeeStatusText, { color: presentation.color }]}
          >
            {item.statusLabel}
          </Text>
        )}
      </View>
      {item.statusNotes?.length ? (
        <Text style={[styles.employeeStatusText, { color: palette.textSecondary }]}>
          {item.statusNotes.join('\n')}
        </Text>
      ) : null}
      <View style={styles.employeeMetaRow}>
        <View style={styles.employeeMetaBlock}>
          <Text
            style={[styles.employeeMetaLabel, { color: palette.textMuted }]}
          >
            Check In
          </Text>
          <Text
            style={[styles.employeeMetaValue, { color: palette.textPrimary }]}
          >
            {formatAttendanceClock(item.checkInTime)}
          </Text>
        </View>
        <View style={styles.employeeMetaBlock}>
          <Text
            style={[styles.employeeMetaLabel, { color: palette.textMuted }]}
          >
            Check Out
          </Text>
          <Text
            style={[styles.employeeMetaValue, { color: palette.textPrimary }]}
          >
            {formatAttendanceClock(item.checkOutTime)}
          </Text>
        </View>
      </View>
      {item.statusReason ? (
        <Text
          style={[styles.employeeMetaValue, { color: palette.textSecondary }]}
        >
          Reason: {item.statusReason}
        </Text>
      ) : null}
      <View style={styles.employeeMetaRow}>
        <View style={styles.employeeMetaBlock}>
          <Text style={[styles.employeeMetaLabel, { color: palette.textMuted }]}>
            Working Hours
          </Text>
          <Text style={[styles.employeeMetaValue, { color: hoursColor }]}>
            {hoursCoverageText
              ? `${worked} · ${hoursCoverageText}`
              : worked}
          </Text>
        </View>
        <View style={styles.employeeMetaBlock}>
          <Text style={[styles.employeeMetaLabel, { color: palette.textMuted }]}>
            Total Break Hours
          </Text>
          <Text
            style={[styles.employeeMetaValue, { color: palette.textPrimary }]}
          >
            {breakHours}
          </Text>
        </View>
        <View style={styles.employeeMetaBlock}>
          <Text style={[styles.employeeMetaLabel, { color: palette.textMuted }]}>
            Expected Work Hours
          </Text>
          <Text
            style={[styles.employeeMetaValue, { color: palette.textPrimary }]}
          >
            {formatPolicyHours(item.expectedWorkMinutes, '--')}
          </Text>
        </View>
        <View style={styles.employeeMetaBlock}>
          <Text style={[styles.employeeMetaLabel, { color: palette.textMuted }]}>
            Total Work Hours
          </Text>
          <Text style={[styles.employeeMetaValue, { color: totalHoursColor }]}>
            {formatTotalWorkHours(historyTotalWorkMinutes(item), '--')}
          </Text>
        </View>
      </View>
      {locationModeLabel(item) ? (
        <View style={styles.employeeMetaBlock}>
          <Text
            style={[styles.employeeMetaLabel, { color: palette.textMuted }]}
          >
            Attendance type
          </Text>
          <Text
            style={[styles.employeeMetaValue, { color: palette.textPrimary }]}
          >
            {locationModeLabel(item)}
          </Text>
        </View>
      ) : null}
    </Pressable>
  );
}

function AttendanceHistoryRow({
  item,
  onPress,
  palette,
}: {
  item: AttendanceHistoryItem;
  onPress: () => void;
  palette: ColorPalette;
}) {
  const late =
    typeof item.lateMinutes === 'number' && item.lateMinutes > 0
      ? `Late ${formatDurationMinutes(item.lateMinutes)}`
      : null;
  const overtime =
    typeof item.overtimeMinutes === 'number' && item.overtimeMinutes > 0
      ? `Overtime ${formatDurationMinutes(item.overtimeMinutes)}`
      : null;
  const hoursCoverage = resolveCheckedOutWorkHoursCoverage(
    isAttendanceCheckedOut(item),
    item.workedMinutes,
    item.expectedWorkMinutes,
  );
  const hoursCoverageText = workHoursCoverageLabel(hoursCoverage);
  const hoursColor =
    hoursCoverage === 'covered'
      ? palette.success
      : hoursCoverage === 'not_covered'
      ? palette.error
      : palette.textSecondary;
  const totalHoursCoverage = resolveTotalHoursCoverage(
    historyTotalWorkMinutes(item),
    item.spreadMinutes,
  );
  const totalHoursColor =
    totalHoursCoverage === 'not_covered' ? palette.error : palette.textSecondary;

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`${formatAttendanceDate(
        item.attendanceDate,
      )}, status ${item.statusLabel}`}
      onPress={onPress}
      style={[
        styles.row,
        { backgroundColor: palette.surface, borderColor: palette.border },
      ]}
    >
      <View style={styles.rowHeader}>
        <View style={styles.dateBlock}>
          <Text style={[styles.date, { color: palette.textPrimary }]}>
            {formatAttendanceDate(item.attendanceDate)}
          </Text>
          <Text style={[styles.weekday, { color: palette.textMuted }]}>
            {formatAttendanceWeekday(item.attendanceDate)}
          </Text>
        </View>
        <AttendanceStatusBadge label={item.statusLabel} decorative />
      </View>
      <Text style={[styles.status, { color: palette.textSecondary }]}>
        Status: {item.statusLabel}
      </Text>
      {item.statusNotes?.length ? (
        <Text style={[styles.meta, { color: palette.textSecondary }]}>
          {item.statusNotes.join('\n')}
        </Text>
      ) : null}
      {item.statusReason ? (
        <Text style={[styles.meta, { color: palette.textSecondary }]}>
          Reason: {item.statusReason}
        </Text>
      ) : null}
      <View style={styles.metaGrid}>
        <Text style={[styles.meta, { color: palette.textSecondary }]}>
          Check-in: {formatAttendanceTimestamp(item.checkInTime, null)}
        </Text>
        <Text style={[styles.meta, { color: palette.textSecondary }]}>
          Check-out: {formatAttendanceTimestamp(item.checkOutTime, null)}
        </Text>
        <Text style={[styles.meta, { color: hoursColor }]}>
          Worked: {formatDurationMinutes(item.workedMinutes)}
          {hoursCoverageText ? ` · ${hoursCoverageText}` : ''}
        </Text>
        <Text style={[styles.meta, { color: palette.textSecondary }]}>
          Total Break Hours: {formatDurationMinutes(item.breakMinutes)}
        </Text>
        <Text style={[styles.meta, { color: palette.textSecondary }]}>
          Expected Work Hours: {formatPolicyHours(item.expectedWorkMinutes, 'Not available')}
        </Text>
        <Text style={[styles.meta, { color: totalHoursColor }]}>
          Total Work Hours: {formatTotalWorkHours(historyTotalWorkMinutes(item), 'Not available')}
        </Text>
        {item.shiftName ? (
          <Text style={[styles.meta, { color: palette.textSecondary }]}>
            Shift: {item.shiftName}
          </Text>
        ) : null}
        {locationModeLabel(item) ? (
          <Text style={[styles.meta, { color: palette.textSecondary }]}>
            Type: {locationModeLabel(item)}
          </Text>
        ) : null}
      </View>
      {late ? (
        <Text style={[styles.flag, { color: palette.warning }]}>{late}</Text>
      ) : null}
      {overtime ? (
        <Text style={[styles.flag, { color: palette.warning }]}>
          {overtime}
        </Text>
      ) : null}
    </Pressable>
  );
}

const LEGEND: Array<{ tone: AttendanceCalendarTone; label: string }> = [
  { tone: 'present', label: 'Present' },
  { tone: 'absent', label: 'LOP' },
  { tone: 'leave', label: 'Leave' },
  { tone: 'wfh', label: 'WFH' },
  { tone: 'holiday', label: 'Holiday' },
  { tone: 'permission', label: 'Permission' },
];

export function AttendanceHistoryScreen({ navigation, route }: Props) {
  const auth = useAuth();
  const palette = useAppColors();
  const rawEmployeeId = route.params?.employeeId;
  const parsedEmployeeId =
    typeof rawEmployeeId === 'number'
      ? rawEmployeeId
      : typeof rawEmployeeId === 'string'
      ? Number(rawEmployeeId)
      : NaN;
  const scopedEmployeeId =
    Number.isInteger(parsedEmployeeId) && parsedEmployeeId > 0
      ? parsedEmployeeId
      : undefined;
  // Admins opening history for a selected employee reuse the employee History/Calendar UI.
  const isEmployeeView =
    !auth.hasPermission(PERMISSIONS.EMPLOYEE_VIEW) || scopedEmployeeId != null;
  const history = useAttendanceHistory(
    scopedEmployeeId != null ? { employeeId: scopedEmployeeId } : undefined,
  );
  const [periodTab, setPeriodTab] = useState<PeriodTab>('Monthly');
  const [employeeTab, setEmployeeTab] = useState<EmployeeViewTab>('History');
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const {
    applyFilters,
    loadHistory,
    refreshHistory,
    resetFilters,
    retry,
    filters,
    holidays,
    items,
    isLoading,
    isRefreshing,
    isLoadingMore,
    error,
    loadMore,
  } = history;
  const defaults = useMemo(() => createDefaultAttendanceFilters(), []);
  const loadedEmployeeId = useRef<number | undefined | null>(null);

  const { year, monthIndex } = useMemo(
    () => parseYearMonth(filters.fromDate),
    [filters.fromDate],
  );

  const selectedItem = useMemo(() => {
    if (!selectedDate) return null;
    return (
      items.find(item => item.attendanceDate.slice(0, 10) === selectedDate) ??
      null
    );
  }, [items, selectedDate]);

  const holidayMap = useMemo(() => holidaysByDate(holidays), [holidays]);

  const selectedHoliday = useMemo(() => {
    if (!selectedDate) return null;
    return holidayMap.get(selectedDate) ?? null;
  }, [holidayMap, selectedDate]);

  const goToMonth = useCallback(
    (nextYear: number, nextMonthIndex: number) => {
      const range = monthDateRange(nextYear, nextMonthIndex);
      applyFilters({
        ...filters,
        fromDate: range.fromDate,
        toDate: range.toDate,
      });
      setSelectedDate(null);
    },
    [applyFilters, filters],
  );

  const onPrevMonth = useCallback(() => {
    const nextMonth = monthIndex === 0 ? 11 : monthIndex - 1;
    const nextYear = monthIndex === 0 ? year - 1 : year;
    goToMonth(nextYear, nextMonth);
  }, [goToMonth, monthIndex, year]);

  const onNextMonth = useCallback(() => {
    const nextMonth = monthIndex === 11 ? 0 : monthIndex + 1;
    const nextYear = monthIndex === 11 ? year + 1 : year;
    goToMonth(nextYear, nextMonth);
  }, [goToMonth, monthIndex, year]);

  // Apply filters returned from the filter screen (manual navigation result).
  useEffect(() => {
    const applied = route.params?.appliedFilters;
    if (!applied) return;
    applyFilters(applied);
    navigation.setParams({ appliedFilters: undefined });
    loadedEmployeeId.current = scopedEmployeeId;
  }, [
    applyFilters,
    navigation,
    route.params?.appliedFilters,
    scopedEmployeeId,
  ]);

  // Initial load — reloads when employee scope changes.
  useEffect(() => {
    if (loadedEmployeeId.current === scopedEmployeeId) return;
    loadedEmployeeId.current = scopedEmployeeId;
    loadHistory();
  }, [loadHistory, scopedEmployeeId]);

  // Leave approvals and attendance corrections happen on other screens.
  // Refresh when the user comes back so the day statuses are current.
  useEffect(() => {
    let left = false;
    const offBlur = navigation.addListener('blur', () => {
      left = true;
    });
    const offFocus = navigation.addListener('focus', () => {
      if (!left) return;
      left = false;
      refreshHistory();
    });
    return () => {
      offBlur();
      offFocus();
    };
  }, [navigation, refreshHistory]);

  const filtersCustomized =
    filters.fromDate !== defaults.fromDate ||
    filters.toDate !== defaults.toDate ||
    Boolean(filters.attendanceMode);

  const themed = useMemo(
    () => ({
      range: {
        ...typography.body,
        color: palette.textSecondary,
        marginBottom: spacing.md,
      },
      legendLabel: { ...typography.caption, color: palette.textSecondary },
      detailLabel: { ...typography.body, color: palette.textPrimary },
      detailValue: {
        ...typography.body,
        color: palette.textPrimary,
        fontWeight: '600' as const,
      },
      selectedTitle: {
        ...typography.sectionTitle,
        color: palette.textPrimary,
        fontWeight: '700' as const,
      },
    }),
    [palette],
  );

  const openFilters = useCallback(() => {
    navigation.navigate(routes.attendanceFilter, {
      currentFilters: filters,
    });
  }, [filters, navigation]);

  const openDetails = useCallback(
    (attendanceDate: string) => {
      navigation.navigate(routes.attendanceDetails, {
        attendanceDate,
        ...(scopedEmployeeId != null ? { employeeId: scopedEmployeeId } : {}),
      });
    },
    [navigation, scopedEmployeeId],
  );

  const footer = useMemo(() => {
    if (!isLoadingMore) return null;
    return (
      <View
        style={styles.footer}
        accessibilityLabel="Loading more attendance records"
      >
        <ActivityIndicator color={palette.primary} />
      </View>
    );
  }, [isLoadingMore, palette.primary]);

  const headerTitle =
    employeeTab === 'Calendar' ? 'Calendar' : 'Attendance History';

  const employeeHeader = (
    <ScreenHeader
      bleed
      title={headerTitle}
      onBack={navigation.goBack}
      right={
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Filters"
          onPress={openFilters}
          hitSlop={8}
        >
          <FilterGlyph color={palette.white} />
        </Pressable>
      }
    />
  );

  const calendarBlock = (
    <AttendanceMonthCalendar
      year={year}
      monthIndex={monthIndex}
      items={items}
      holidays={holidays}
      selectedDate={selectedDate}
      highlightMode={employeeTab === 'Calendar' ? 'dot' : 'fill'}
      onSelectDate={setSelectedDate}
      onPrevMonth={onPrevMonth}
      onNextMonth={onNextMonth}
    />
  );

  const legendBlock = (
    <View
      style={[
        styles.legendCard,
        { backgroundColor: palette.surface, borderColor: palette.border },
      ]}
    >
      {LEGEND.map(entry => (
        <View key={entry.label} style={styles.legendItem}>
          <View
            style={[
              styles.legendDot,
              { backgroundColor: attendanceToneColor(entry.tone, palette) },
            ]}
          />
          <Text style={themed.legendLabel}>{entry.label}</Text>
        </View>
      ))}
    </View>
  );

  const selectedDetailBlock = (
    <View
      style={[
        styles.detailCard,
        { backgroundColor: palette.surface, borderColor: palette.border },
      ]}
    >
      <Text style={themed.selectedTitle}>
        {selectedDate
          ? formatAttendanceListDate(selectedDate)
          : 'Select a date'}
      </Text>
      {selectedDate ? (
        selectedItem || selectedHoliday ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={`Open details for ${formatAttendanceListDate(
              selectedDate,
            )}`}
            onPress={() => {
              if (selectedItem?.attendanceId) {
                openDetails(selectedDate);
              }
            }}
          >
            <View style={styles.detailRow}>
              <DetailRowIcon kind="checkIn" color={palette.textMuted} />
              <Text style={themed.detailLabel}>Check In</Text>
              <Text style={themed.detailValue}>
                {formatAttendanceClock(selectedItem?.checkInTime)}
              </Text>
            </View>
            <View
              style={[
                styles.detailDivider,
                { backgroundColor: palette.border },
              ]}
            />
            <View style={styles.detailRow}>
              <DetailRowIcon kind="checkOut" color={palette.textMuted} />
              <Text style={themed.detailLabel}>Check Out</Text>
              <Text style={themed.detailValue}>
                {formatAttendanceClock(selectedItem?.checkOutTime)}
              </Text>
            </View>
            <View
              style={[
                styles.detailDivider,
                { backgroundColor: palette.border },
              ]}
            />
            <View style={styles.detailRow}>
              <DetailRowIcon kind="checkIn" color={palette.textMuted} />
              <Text style={themed.detailLabel}>Total Break Hours</Text>
              <Text style={themed.detailValue}>
                {formatHistoryDuration(selectedItem?.breakMinutes)}
              </Text>
            </View>
            <View
              style={[
                styles.detailDivider,
                { backgroundColor: palette.border },
              ]}
            />
            <View style={styles.detailRow}>
              <DetailRowIcon kind="checkIn" color={palette.textMuted} />
              <Text style={themed.detailLabel}>Expected Work Hours</Text>
              <Text style={themed.detailValue}>
                {formatPolicyHours(selectedItem?.expectedWorkMinutes, '--')}
              </Text>
            </View>
            <View
              style={[
                styles.detailDivider,
                { backgroundColor: palette.border },
              ]}
            />
            <View style={styles.detailRow}>
              <DetailRowIcon kind="checkIn" color={palette.textMuted} />
              <Text style={themed.detailLabel}>Total Work Hours</Text>
              <Text
                style={[
                  themed.detailValue,
                  {
                    color:
                      resolveTotalHoursCoverage(
                        selectedItem
                          ? historyTotalWorkMinutes(selectedItem)
                          : null,
                        selectedItem?.spreadMinutes,
                      ) === 'not_covered'
                        ? palette.error
                        : palette.textPrimary,
                  },
                ]}
              >
                {formatTotalWorkHours(
                  selectedItem ? historyTotalWorkMinutes(selectedItem) : null,
                  '--',
                )}
              </Text>
            </View>
            <View
              style={[
                styles.detailDivider,
                { backgroundColor: palette.border },
              ]}
            />
            <View style={styles.detailRow}>
              <DetailRowIcon kind="status" color={palette.textMuted} />
              <Text style={themed.detailLabel}>Status</Text>
              <Text
                style={[
                  themed.detailValue,
                  {
                    color: selectedItem
                      ? attendanceToneColor(
                          resolveAttendanceCalendarTone(selectedItem),
                          palette,
                        )
                      : attendanceToneColor('holiday', palette),
                  },
                ]}
              >
                {selectedItem?.statusLabel ??
                  (selectedHoliday ? 'Holiday' : '--')}
              </Text>
            </View>
            {selectedItem?.statusReason || selectedHoliday?.holidayName ? (
              <>
                <View
                  style={[
                    styles.detailDivider,
                    { backgroundColor: palette.border },
                  ]}
                />
                <View style={styles.detailRow}>
                  <DetailRowIcon kind="status" color={palette.textMuted} />
                  <Text style={themed.detailLabel}>Reason</Text>
                  <Text style={themed.detailValue}>
                    {selectedItem?.statusReason ?? selectedHoliday?.holidayName}
                  </Text>
                </View>
              </>
            ) : null}
            {selectedItem && locationModeLabel(selectedItem) ? (
              <>
                <View
                  style={[
                    styles.detailDivider,
                    { backgroundColor: palette.border },
                  ]}
                />
                <View style={styles.detailRow}>
                  <DetailRowIcon kind="status" color={palette.textMuted} />
                  <Text style={themed.detailLabel}>Type</Text>
                  <Text style={themed.detailValue}>
                    {locationModeLabel(selectedItem)}
                  </Text>
                </View>
              </>
            ) : null}
          </Pressable>
        ) : (
          <Text style={[styles.meta, { color: palette.textMuted }]}>
            No data available for this day.
          </Text>
        )
      ) : (
        <Text style={[styles.meta, { color: palette.textMuted }]}>
          Tap a day on the calendar to see check-in details.
        </Text>
      )}
    </View>
  );

  if (isEmployeeView) {
    return (
      <PermissionGuard
        permission={PERMISSIONS.ATTENDANCE_VIEW}
        fallback={<UnauthorizedScreen />}
      >
        <AppScreen contentStyle={styles.screen} webLayout="wide">
          {employeeHeader}
          <View style={styles.body}>
            <SegmentedControl
              options={['History', 'Calendar'] as const}
              value={employeeTab}
              onChange={setEmployeeTab}
            />

            {isLoading && items.length === 0 ? (
              <View style={styles.center}>
                <ActivityIndicator
                  color={palette.primary}
                  accessibilityLabel="Loading attendance history"
                />
              </View>
            ) : error && items.length === 0 ? (
              <ErrorState message={error} onRetry={retry} />
            ) : employeeTab === 'History' ? (
              <FlatList
                data={items}
                keyExtractor={rowKey}
                refreshControl={
                  <RefreshControl
                    refreshing={isRefreshing}
                    onRefresh={refreshHistory}
                    tintColor={palette.primary}
                  />
                }
                onEndReached={loadMore}
                onEndReachedThreshold={0.4}
                ListHeaderComponent={
                  <View style={styles.listHeader}>{calendarBlock}</View>
                }
                ListFooterComponent={footer}
                ListEmptyComponent={
                  <EmptyState
                    title="No data available"
                    message="No attendance records were found for this period."
                    actionTitle={filtersCustomized ? 'Reset filters' : 'Retry'}
                    onAction={filtersCustomized ? resetFilters : retry}
                  />
                }
                contentContainerStyle={styles.listContent}
                renderItem={({ item }) => (
                  <EmployeeHistoryRow
                    item={item}
                    palette={palette}
                    onPress={() => openDetails(item.attendanceDate)}
                  />
                )}
              />
            ) : (
              <ScrollView
                refreshControl={
                  <RefreshControl
                    refreshing={isRefreshing}
                    onRefresh={refreshHistory}
                    tintColor={palette.primary}
                  />
                }
                contentContainerStyle={styles.listContent}
              >
                <View style={styles.calendarStack}>
                  {calendarBlock}
                  {legendBlock}
                  {selectedDetailBlock}
                </View>
              </ScrollView>
            )}
          </View>
        </AppScreen>
      </PermissionGuard>
    );
  }

  return (
    <PermissionGuard
      permission={PERMISSIONS.ATTENDANCE_VIEW}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen contentStyle={styles.screen} webLayout="wide">
        <ScreenHeader title="Attendance History" onBack={navigation.goBack} />
        <View style={styles.body}>
          <SegmentedControl
            options={['Daily', 'Weekly', 'Monthly'] as const}
            value={periodTab}
            onChange={setPeriodTab}
          />
          <Text style={themed.range}>
            {filters.fromDate} to {filters.toDate}
            {filters.attendanceMode ? ` · Mode ${filters.attendanceMode}` : ''}
          </Text>
          <View style={styles.actions}>
            <AppButton
              title="Filters"
              variant="secondary"
              onPress={openFilters}
            />
          </View>

          {isLoading && items.length === 0 ? (
            <View style={styles.center}>
              <ActivityIndicator
                color={palette.primary}
                accessibilityLabel="Loading attendance history"
              />
            </View>
          ) : error && items.length === 0 ? (
            <ErrorState message={error} onRetry={retry} />
          ) : (
            <FlatList
              data={items}
              keyExtractor={rowKey}
              refreshControl={
                <RefreshControl
                  refreshing={isRefreshing}
                  onRefresh={refreshHistory}
                  tintColor={palette.primary}
                />
              }
              onEndReached={loadMore}
              onEndReachedThreshold={0.4}
              ListFooterComponent={footer}
              ListEmptyComponent={
                <EmptyState
                  title="No data available"
                  message="No attendance records were found for this period."
                  actionTitle={filtersCustomized ? 'Reset filters' : 'Retry'}
                  onAction={filtersCustomized ? resetFilters : retry}
                />
              }
              contentContainerStyle={styles.listContent}
              renderItem={({ item }) => (
                <AttendanceHistoryRow
                  item={item}
                  palette={palette}
                  onPress={() => openDetails(item.attendanceDate)}
                />
              )}
            />
          )}
        </View>
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, paddingBottom: 0 },
  body: { flex: 1, marginTop: spacing.lg, gap: spacing.md },
  actions: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  listContent: { flexGrow: 1, paddingBottom: spacing.xl, gap: spacing.sm },
  listHeader: { marginBottom: spacing.md },
  calendarStack: { gap: spacing.md },
  row: {
    borderWidth: 1,
    borderRadius: 16,
    padding: spacing.lg,
    gap: spacing.sm,
  },
  rowHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: spacing.md,
  },
  dateBlock: { flex: 1, gap: spacing.xs },
  date: { ...typography.body, fontWeight: '700' },
  weekday: { ...typography.caption },
  status: { ...typography.caption },
  metaGrid: { gap: spacing.xs },
  meta: { ...typography.caption },
  flag: { ...typography.caption, fontWeight: '600' },
  footer: { paddingVertical: spacing.lg, alignItems: 'center' },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  filterGlyph: {
    width: 18,
    height: 18,
    alignItems: 'center',
  },
  filterFunnel: {
    width: 0,
    height: 0,
    borderLeftWidth: 8,
    borderRightWidth: 8,
    borderTopWidth: 10,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
  },
  filterStem: {
    width: 2,
    height: 6,
    marginTop: -1,
  },
  employeeRow: {
    borderWidth: 1,
    borderRadius: 16,
    padding: spacing.lg,
    gap: spacing.md,
  },
  employeeRowHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: spacing.md,
  },
  employeeDate: { ...typography.body, fontWeight: '700', flex: 1 },
  employeeStatusText: { ...typography.caption, fontWeight: '700' },
  employeeMetaRow: {
    flexDirection: 'row',
    gap: spacing.lg,
  },
  employeeMetaBlock: { flex: 1, gap: 2 },
  employeeMetaLabel: { ...typography.caption },
  employeeMetaValue: { ...typography.body, fontWeight: '600' },
  legendCard: {
    borderWidth: 1,
    borderRadius: 16,
    padding: spacing.lg,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
  },
  legendItem: {
    width: '45%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  detailCard: {
    borderWidth: 1,
    borderRadius: 16,
    padding: spacing.lg,
    gap: spacing.md,
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingVertical: spacing.sm,
  },
  detailDivider: { height: StyleSheet.hairlineWidth },
  detailIcon: {
    width: 28,
    height: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },
  clockOuter: {
    width: 18,
    height: 18,
    borderRadius: 9,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  clockHand: {
    position: 'absolute',
    width: 1.5,
    height: 6,
    borderRadius: 1,
    top: 3,
  },
  clockHandShort: {
    position: 'absolute',
    width: 5,
    height: 1.5,
    borderRadius: 1,
    left: 9,
  },
  clipboardBox: {
    width: 14,
    height: 16,
    borderWidth: 1.5,
    borderRadius: 2,
    alignItems: 'center',
    paddingTop: 4,
    gap: 2,
  },
  clipboardClip: {
    position: 'absolute',
    top: -3,
    width: 8,
    height: 4,
    borderRadius: 1,
  },
  clipboardLine: {
    width: 8,
    height: 1.5,
    borderRadius: 1,
  },
});
